import { useState, useEffect, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { Container, Row, Col, Breadcrumb, Pagination, Spinner } from "react-bootstrap";
import WhyUs from "../sections/WhyUs";
import Inquiry  from "../sections/Inquiry";
import PropertyFilter from "../components/PropertyFilter";
import PropertyCard from "../components/PropertyCard";
import { createQueryParams, normalizeFilters } from "../Functions";
import '../assets/scss/pages/listings.scss';

const Listings = () =>  {
    const API = process.env.REACT_APP_API_BASE_URL;
    const [listings, setListings] = useState([]);
    const [fetchFeedBack, setFetchFeedback] = useState({ success: null, message: ""});
    const [prev, setPrev] = useState("");
    const [next, setNext] = useState("");
    const [totalPages, setTotalPages] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [isLoading, setIsLoading] = useState(false);
    const location = useLocation();
    const [filters, setFilters] = useState(() => location.state?.filters || {}); // {} ensures filter is cleared on refresh
    // console.log(`filters received: ${filters}`);

    const fetchListings = useCallback(async (pageNumber, currentFilters = {}) => {
        setIsLoading(true);

        try {
            const normalizedFilters = normalizeFilters(currentFilters);
            const filterString = createQueryParams(normalizedFilters);

            const url = `${API}/properties/?page=${pageNumber}&${filterString}`;
            const response = await fetch(url);
            if (!response.ok) {
                const errorData = await response.json();
                setFetchFeedback({ success: false, message: "Unable to load properties, please refresh page." });
                console.error(`Error fetching properties: ${errorData?.detail}` || `Error fetching properties: ${errorData.status}`);
            }

            const data = await response.json();
            setListings(data.results);

            setPrev(data.links["prev"]);
            setNext(data.links["next"]);
            setCurrentPage(data.current_page);
            setTotalPages(data.total_pages);

            setFetchFeedback({ success: true, message: ""});
        } catch (error) {
            setFetchFeedback({ success: false, message: "An error has occured." });
            console.error(error);
        } finally {
            setIsLoading(false);
        }
    }, [API]);

    useEffect(() => {
        fetchListings(currentPage, filters);
    }, [currentPage, filters, fetchListings]);

    useEffect(() => {
        setFilters(location.state?.filters || {}); // Update filters on location change
    }, [location]);

    // Function to handle change in filter fields
    const handleFilterChange = (newFilters) => {
        setFilters(newFilters);
    }

    // Pagination logic
    const handlePageClick = (pageNumber) => {
        setCurrentPage(pageNumber);
    }

    const pages = [];
    for (let pageNumber = 1; pageNumber <= totalPages; pageNumber++) {
        pages.push(
            <Pagination.Item
                key={ pageNumber }
                active={ pageNumber === currentPage }
                onClick={ () => handlePageClick(pageNumber) }
            >
                { pageNumber }
            </Pagination.Item>
        )
    }

    return (
        <>
            <Container className="flex-grow-1 listings">
                {/* Navigation section */}
                <Breadcrumb className="py-5 mx-auto">
                    <Breadcrumb.Item href="/">Home</Breadcrumb.Item>
                    <Breadcrumb.Item active className="text-primary">Property Listings</Breadcrumb.Item>
                </Breadcrumb>

                <header className="pb-5">
                    <h2 className="text-center text-primary py-3"><b>PROPERTY</b> LISTINGS</h2>
                    <p className="text-center">Discover some of our finest listings</p>
                </header>

                {/* Filter section*/}
                <Row as="section" className="mx-auto pb-5">
                    <PropertyFilter filters={ filters } onFilterChange={ handleFilterChange } />
                </Row>

                {/* Properties Section */}
                <Row as="section" className="listings-section justify-content-center mx-auto">
                    {/* Listings */}
                    <Row as="section" xs={1} sm={1} md={2} lg={3} xl={3} xxl={3} className="properties gy-4 mx-auto mb-5 px-0">
                        {isLoading ? (
                            <div className="text-center mx-auto mt-5">
                                <Spinner animation="border" variant="primary" className="mx-auto" />
                                <p className="mt-2">Loading properties...</p>
                            </div>
                        ) : fetchFeedBack.success ? (
                                listings?.length > 0 ?
                                    (listings.map((listing, index) => (
                                        <Col key={index}>
                                            <PropertyCard property={ listing } />
                                        </Col>
                                    )))
                                    : <p className="text-center text-primary fw-semibold mx-auto pt-5">
                                      No properties found.
                                    </p>
                                )
                            : (
                                <p className="text-center mx-auto fw-semibold pt-5 text-danger">
                                    { fetchFeedBack.message }
                                </p>
                            )
                        }
                    </Row>

                    {/* Pagination */}
                    { totalPages > 1 &&
                        <Pagination size="sm" className="gap-2 justify-content-center mx-auto my-3 my-lg-4 my-xl-5">
                            <Pagination.Prev onClick={() => prev && setCurrentPage(currentPage - 1)} disabled={!prev}>Prev</Pagination.Prev>
                            { pages }
                            <Pagination.Next onClick={() => next && setCurrentPage(currentPage + 1)} disabled={!next}>Next</Pagination.Next>
                        </Pagination>
                    }
                </Row>
            </Container>

            {/* Why Us section*/}
            <WhyUs />

            <Container className="flex-grow-1 listings">
                {/* Inquiry section */}
                <Inquiry contactCardType="icons" />
            </Container>
        </>
    );
};

export default Listings;
